/**
 * Shared helpers for metaobject alias generation and GraphQL fragment building.
 * Used by SearchProvider (and its subclasses) and shopify.js to ensure the alias
 * format and fragment structure are defined in exactly one place.
 */

/**
 * Return the GraphQL alias for a 3-part metafield identifier.
 * Format: metaobj_{namespace}_{key}_{metaobjectField}  (non-alphanumeric → underscore)
 *
 * @param {{ namespace: string, key: string, metaobjectField: string }} id
 * @returns {string}
 */
export function metaobjectAlias({ namespace, key, metaobjectField }) {
  return `metaobj_${namespace}_${key}_${metaobjectField}`.replace(/[^a-zA-Z0-9_]/g, '_');
}

/**
 * Build inline GraphQL fields for all 3-part metaobject identifiers present in
 * window.productMetafieldIdentifiers (those with a metaobjectField property).
 *
 * The resulting string can be interpolated directly into a product node query so
 * that transformGraphqlProduct() can decode the aliased fields back into the
 * normalised { namespace, key, value } format.
 *
 * @param {Array} [identifiers] - Optional override; defaults to window.productMetafieldIdentifiers filtered to 3-part entries
 * @returns {string} GraphQL field string (empty string when there are no 3-part identifiers)
 */
export function buildMetaobjectFragment(identifiers) {
  const ids = identifiers ?? (window.productMetafieldIdentifiers || []).filter(id => id.metaobjectField);
  if (!ids.length) return '';

  return ids.map(id => {
    const alias = metaobjectAlias(id);
    return `
        ${alias}: metafield(namespace: "${id.namespace}", key: "${id.key}") {
          reference {
            ... on Metaobject {
              field(key: "${id.metaobjectField}") { value }
            }
          }
        }`;
  }).join('\n');
}

/**
 * Decode the aliased metaobject fields requested by buildMetaobjectFragment() from a
 * product node into normalised metafield entries. Each entry carries its metaobject
 * `field` so several fields of the same metaobject reference can coexist.
 *
 * @param {Object} node - Product node returned by the Storefront API
 * @param {Array} [existing] - Already-normalised metafields, used to skip duplicates
 * @returns {Array<{ namespace: string, key: string, field: string, type: string, value: Array }>}
 */
export function decodeMetaobjectFields(node, existing = []) {
  if (!node) return [];

  return (window.productMetafieldIdentifiers || [])
    .filter(id => id.metaobjectField)
    .map(id => {
      const aliasedField = node[metaobjectAlias(id)];
      if (!aliasedField) return null;
      const resolvedValue = aliasedField.reference?.field?.value ?? null;
      return {
        namespace: id.namespace,
        key: id.key,
        field: id.metaobjectField,
        type: 'single_line_text_field',
        value: resolvedValue != null ? [resolvedValue] : []
      };
    })
    .filter(Boolean)
    .filter(entry => !existing.some(m => m.namespace === entry.namespace && m.key === entry.key && m.field === entry.field));
}
